import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { ALERT_LIFECYCLE_RESOLVED } from '../../../constants.ts';
import { patchAlert } from '../../../queries/getUnresolvedAlert.ts';
import { Alert } from '../../../schema/Alert.ts';
import { FormatTime, getAlertWording } from '../../../utils/utils.ts';
import { RoundedButton } from '../../ui/Button.tsx';
import { OverlayTooltip } from '../../ui/Tooltip.tsx';

export function AlertCard(props: Readonly<{ alert: Alert }>) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const params = useParams() as { territorySlug: string };

  const mutation = useMutation({
    mutationFn: (payload: { lifecycle: string }) =>
      patchAlert(params.territorySlug, props.alert.id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alert', params.territorySlug] });
      toast.success(`${t('alert.alert-resolve-success')}`);
    },
    meta: { errorToastKey: 'alert.alert-resolve-fail' },
  });

  function closeAlert() {
    mutation.mutate({ lifecycle: ALERT_LIFECYCLE_RESOLVED });
  }

  return (
    <div className={'row rounded-4 mb-2 bg-white basic-shadow py-2 m-0 p-0 border-danger'}>
      <div className={'col-2 my-auto text-center'}>
        <img src={'/theme/media/red-warning.svg'} width="30" alt="alert icon" />
      </div>
      <div className={'col-8 text-start p-0'}>
        <p className={'text-basic'}>{props.alert.get_content_object_display_name}</p>
        <p className={'card-text my-auto'}>
          {getAlertWording(props.alert.code)}
          <OverlayTooltip text={JSON.stringify(props.alert.extra)}>
            <img
              src={'/theme/media/info.svg'}
              width="15"
              height="15"
              className={'ms-1'}
              alt="Info icon"
            />
          </OverlayTooltip>
        </p>
        <p className={'text-basic'}>
          {FormatTime(props.alert.created_at, false)} | {props.alert.events} {t('alert.events')}
        </p>
      </div>
      <div className={'col-2 my-auto text-center'}>
        <RoundedButton
          type={'rounded-button'}
          text={'Ok'}
          imagePath={null}
          loading={mutation.isPending}
          onClick={closeAlert}
        />
      </div>
    </div>
  );
}
